import { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

const MAX_TOASTS = 3;
const DISMISS_MS = 3500;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    const id = ++idRef.current;
    setToasts((prev) => {
      const next = [...prev, { id, message, type }];
      return next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next;
    });
    setTimeout(() => dismissToast(id), DISMISS_MS);
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;

  return (
    <div style={styles.container}>
      {toasts.map((t) => (
        <div key={t.id} style={{ ...styles.toast, ...styles[t.type] }}>
          <span style={styles.icon}>
            {t.type === 'error' ? '✕' : t.type === 'success' ? '✓' : 'ℹ'}
          </span>
          <span style={styles.message}>{t.message}</span>
          <button style={styles.close} onClick={() => onDismiss(t.id)}>×</button>
        </div>
      ))}
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    top: 72,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '90%',
    maxWidth: 400,
    pointerEvents: 'none',
  },
  toast: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
    pointerEvents: 'auto',
    animation: 'slideInUp 200ms ease-out',
  },
  error: {
    background: '#1a2332',
    border: '1px solid rgba(239,68,68,0.4)',
    color: '#fca5a5',
  },
  success: {
    background: '#1a2332',
    border: '1px solid rgba(34,197,94,0.4)',
    color: '#86efac',
  },
  info: {
    background: '#1a2332',
    border: '1px solid rgba(232,87,109,0.4)',
    color: '#f0eee9',
  },
  icon: {
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  message: {
    flex: 1,
    lineHeight: 1.4,
  },
  close: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    opacity: 0.5,
    fontSize: 18,
    cursor: 'pointer',
    padding: '4px 8px',
    flexShrink: 0,
  },
};
